import { Box, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import ProviderIntegrationCard, { PROVIDERS } from "./ProviderIntegrationCard";

// "Integraatio" sub-tab of the accounting settings: direct accounting software
// integrations (Netvisor, Fennoa, Procountor). Only one provider can be active
// per organization; the backend deactivates the previous one on activation.
export default function AccountingIntegrationSubTab() {
  const { t } = useTranslation();
  const { currentOrganization, user } = useUserStore();
  const organizationId = currentOrganization?.organization_id;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // Unpublished providers (PROVIDERS[x].staffOnly) stay staff-only until their
  // adapter has been verified live; the backend enforces the same list.
  const visibleProviders = ["netvisor", "fennoa", "procountor"].filter(
    (provider) => isStaff || !PROVIDERS[provider]?.staffOnly
  );

  // The org's currently active provider (null when none). Passed to each card
  // so activating another provider can warn about the switch.
  const [activeProvider, setActiveProvider] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchActiveProvider = useCallback(async () => {
    if (!organizationId) return;
    try {
      const resp = await axios.get(`/api/accounting-integrations/integration/?organization=${organizationId}`);
      setActiveProvider(resp.data?.configured && resp.data?.is_active ? resp.data.provider : null);
    } catch (e) {
      console.error("Failed to load active accounting integration", e);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchActiveProvider();
  }, [fetchActiveProvider]);

  // After any card activates/deactivates, refresh the active provider and make
  // every card refetch its own state (activation deactivates siblings server-side).
  const handleStatusChange = useCallback(() => {
    fetchActiveProvider();
    setRefreshToken((token) => token + 1);
  }, [fetchActiveProvider]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          {t("accounting.integrationTitle", "Kirjanpitointegraatio")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("accounting.integrationDescription", "Siirrä laskut automaattisesti kirjanpito-ohjelmaan.")}
        </Typography>
      </Box>

      {/* Provider cards grid (same anatomy as OrganizationIntegrationsTab) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        {visibleProviders.map((provider) => (
          <ProviderIntegrationCard
            key={provider}
            provider={provider}
            activeProvider={activeProvider}
            refreshToken={refreshToken}
            onStatusChange={handleStatusChange}
          />
        ))}
      </Box>
    </Box>
  );
}
